import { FunctionComponent, ReactNode } from "react";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { classNames } from "@/utils/classNames";

import { Icon } from "../Icon/Icon";

import { TableColumn } from "./Table.types";

/** An opt-in "+" add action rendered by the Table (sticky add-column column / add-lead row). */
export interface TableAddAction {
	onAdd: () => void;
	label: string;
}

/**
 * The whole-column hover tint works without re-renders: every cell of the "+" column carries the
 * `table-addcol` marker, and `group-has-[.table-addcol:hover]/table:*` variants (anchored on the
 * `group/table` class on the `<table>`) light all of them up when any one is hovered (PREC-3703).
 */
const HOT_BG = "group-has-[.table-addcol:hover]/table:bg-blue-action-200";

/** Sticky right header cell of the "+" column: persistent plus, hover-revealed label pill. */
export const AddColumnHeaderCell: FunctionComponent<{
	addColumn: TableAddAction;
	/** Whether the table header row is sticky (the cell then pins to the top-right corner). */
	stickyTop: boolean;
}> = ({ addColumn, stickyTop }) => (
	<th
		className={classNames(
			// min-w matters: the lead-list table is over-constrained (w-full with columns forcing
			// horizontal overflow), which squeezes every column to its minimum — width alone
			// collapses this cell to the icon's width.
			"table-addcol sticky right-0 w-[52px] min-w-[52px] border-b border-l border-border-light bg-brand-white p-0",
			stickyTop ? "top-0" : "",
			HOT_BG
		)}
		style={{ zIndex: stickyTop ? 130 : 110 }}
	>
		<button
			className="relative flex h-full min-h-[34px] w-full min-w-[52px] cursor-pointer items-center justify-center text-text-meta group-has-[.table-addcol:hover]/table:text-blue-action"
			data-testid="table-add-column"
			title={addColumn.label}
			type="button"
			onClick={addColumn.onAdd}
		>
			<Icon className="h-3.5 w-3.5" icon={faPlus} />
			<span className="pointer-events-none absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-lg bg-blue-action px-2.5 py-1 text-xs font-semibold normal-case text-white opacity-0 shadow-md transition-opacity group-has-[.table-addcol:hover]/table:opacity-100">
				{addColumn.label}
			</span>
		</button>
	</th>
);

/** Sticky right body cell of the "+" column — an empty, clickable extension of the header "+". */
export const AddColumnBodyCell: FunctionComponent<{ onAdd: () => void }> = ({ onAdd }) => (
	<td
		className={classNames(
			"table-addcol sticky right-0 min-w-[52px] cursor-pointer border-b border-l border-border-light bg-white p-0",
			HOT_BG
		)}
		style={{ zIndex: 110 }}
		onClick={(event) => {
			// The row itself may be clickable (opens the lead panel) — don't let the add-column
			// click double as a row click.
			event.stopPropagation();
			onAdd();
		}}
	/>
);

/**
 * The table's `<tfoot>`: the pre-existing per-column `footerRow` (extracted from Table.tsx) and/or
 * the sticky-bottom "+ Add lead" row (PREC-3703). A table allows only one `<tfoot>`, so both render
 * here; it pins to the scroll viewport's bottom edge only when the add row needs it.
 */
export const TableFoot: FunctionComponent<{
	columns: TableColumn[];
	footerRow?: ReactNode[];
	addRow?: TableAddAction;
	hasAddColumn: boolean;
}> = ({ columns, footerRow, addRow, hasAddColumn }) => {
	const visibleColumns = columns.filter((c) => c.show || c.show === undefined);

	return (
		<tfoot
			className={addRow ? "sticky bottom-0" : undefined}
			style={addRow ? { zIndex: 115 } : undefined}
		>
			{footerRow && (
				<tr className="bg-background-light">
					{visibleColumns.map((column, index) => {
						const originalIndex = columns.indexOf(column);
						const { align = "left" } = column;

						return (
							<td
								key={`footer-${index}`}
								className={classNames(
									"px-3 py-3 text-sm border-t border-border-light",
									align === "left" ? "text-left" : "text-right"
								)}
							>
								{footerRow[originalIndex] ?? null}
							</td>
						);
					})}
					{hasAddColumn && <td className="border-t border-border-light" />}
				</tr>
			)}
			{addRow && (
				<tr>
					<td className="p-0" colSpan={visibleColumns.length + (hasAddColumn ? 1 : 0)}>
						<button
							className="flex h-11 w-full cursor-pointer items-center gap-2.5 border-t border-border-light bg-white px-4 text-sm font-semibold text-text-meta transition-colors hover:bg-blue-action-200 hover:text-blue-action"
							data-testid="table-add-row"
							type="button"
							onClick={addRow.onAdd}
						>
							<Icon className="h-3.5 w-3.5" icon={faPlus} />
							{addRow.label}
						</button>
					</td>
				</tr>
			)}
		</tfoot>
	);
};
